import { CalendarMonth, Send, SmsFailed } from "@mui/icons-material";
import { Badge, Box, Tab, Tabs } from "@mui/material";
import { ReactNode } from "react";
import { IGroupMessageView } from "../../../App/Typings/MessageText";
import { ActionConfirmationDialog } from "../../ScheduledMessages/ActionConfirmationDialog";
import { MessageTable } from "../../ScheduledMessages/MessageTable";
import MessageEmailHistoryTabState from "./State/MessageEmailHistoryTabState";
import MoveToInboxIcon from "@mui/icons-material/MoveToInbox";

interface TabPanelProps {
  children?: ReactNode;
  index: number;
  value: number;
}

function historyListProps(index: number) {
  return {
    id: `email-history-tab-${index}`,
    "aria-controls": `email-history-tabpanel-${index}`,
  };
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`email-history-tabpanel-${index}`}
      aria-labelledby={`email-history-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

export default function MessageEmailHistoryTabs({ candidates }: { candidates: string[] }) {
  const {
    handlers: { deleteSelectedScheduledMessages, handleChange, handleConfirmationResponse },
    getters: {
      errorMessages,
      receivedMessages,
      sentMessages,
      schMessages,
      tabValue,
      isLoadingScheduledMessages,
      columnsWithActionButtons,
      deleteScheduledMessageIsLoading,
      showConfirmDialog,
      actionMessage,
      columnsNoActionButtons,
      columnsNoActionButtonsForErrors,
    },
  } = MessageEmailHistoryTabState(candidates.length ? candidates[0] : "");

  const handleDeleteExistingMessage = (msg: IGroupMessageView) => {
    deleteSelectedScheduledMessages(msg);
  };

  return (
    <Box sx={{ width: "100%" }}>
      <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
        <Tabs value={tabValue} onChange={handleChange} aria-label="basic tabs example">
          <Tab
            icon={
              <Badge
                badgeContent={schMessages.length}
                color="primary"
                showZero={false}
                max={999}
                data-test="message-scheduled-badge"
              >
                <CalendarMonth />
              </Badge>
            }
            label={"Scheduled"}
            {...historyListProps(0)}
          />
          <Tab
            icon={
              <Badge
                badgeContent={sentMessages.length}
                color="primary"
                showZero={false}
                max={999}
                data-test="message-sent-badge"
              >
                <Send />
              </Badge>
            }
            label={"Sent"}
            {...historyListProps(1)}
          />
          <Tab
            icon={
              <Badge
                badgeContent={receivedMessages.length}
                color="primary"
                showZero={false}
                max={999}
                data-test="message-received-badge"
              >
                <MoveToInboxIcon />
              </Badge>
            }
            label={"Received"}
            {...historyListProps(2)}
          />
          <Tab
            icon={
              <Badge
                badgeContent={errorMessages.length}
                color="error"
                showZero={false}
                max={999}
                data-test="message-exception-badge"
              >
                <SmsFailed />
              </Badge>
            }
            label={"Exceptions"}
            {...historyListProps(3)}
          />
        </Tabs>
      </Box>
      <TabPanel value={tabValue} index={0}>
        <MessageTable
          id="email-history-schedule-message-table"
          loading={isLoadingScheduledMessages}
          columns={columnsWithActionButtons}
          tableData={schMessages}
          isEditingGroup={false}
          deleteSelectedScheduledMessages={handleDeleteExistingMessage}
          showActions={true}
          disableDeleteButton={deleteScheduledMessageIsLoading}
        />
      </TabPanel>
      <TabPanel value={tabValue} index={1}>
        <MessageTable
          id="email-history-sent-message-table"
          loading={isLoadingScheduledMessages}
          columns={columnsNoActionButtons}
          tableData={sentMessages}
          isEditingGroup={false}
          deleteSelectedScheduledMessages={handleDeleteExistingMessage}
          showActions={false}
          disableDeleteButton={deleteScheduledMessageIsLoading}
        />
      </TabPanel>
      <TabPanel value={tabValue} index={2}>
        <MessageTable
          id="email-history-received-message-table"
          loading={isLoadingScheduledMessages}
          columns={columnsNoActionButtons}
          tableData={receivedMessages}
          isEditingGroup={false}
          deleteSelectedScheduledMessages={handleDeleteExistingMessage}
          showActions={false}
          disableDeleteButton={deleteScheduledMessageIsLoading}
        />
      </TabPanel>
      <TabPanel value={tabValue} index={3}>
        <MessageTable
          id="email-history-error-message-table"
          loading={isLoadingScheduledMessages}
          columns={columnsNoActionButtonsForErrors}
          tableData={errorMessages}
          isEditingGroup={false}
          deleteSelectedScheduledMessages={handleDeleteExistingMessage}
          showActions={false}
          disableDeleteButton={deleteScheduledMessageIsLoading}
        />
      </TabPanel>
      <TabPanel value={tabValue} index={4}></TabPanel>
      <ActionConfirmationDialog
        handleClose={handleConfirmationResponse}
        open={showConfirmDialog}
        message={actionMessage}
      />
    </Box>
  );
}
